/**
 * @fileoverview Profit Over Time area chart component using ShadCn Charts (Recharts) and derived dashboard data
 * @module shared/components/ui/ProfitOverTimeChart
 */

import { type ReactElement, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import { pl, enUS } from 'date-fns/locale'
import { Area, AreaChart, CartesianGrid, ReferenceDot, ReferenceLine, XAxis, YAxis } from 'recharts'
import { TrendingUp } from 'lucide-react'
import { ChartEmptyState } from './ChartEmptyState'
import { cn } from '@/shared/lib/utils'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import { Card } from '@/components/ui/card'
import { useProfitOverTime, useTopProductsByPeriod } from '@/shared/hooks/dashboard'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useProductGroupsMetadata } from '@/shared/hooks'
import { formatMoney } from '@/shared/utils/money'
import { useFilter } from '@/shared/hooks/useFilter'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'
import { formatIsoWeekRange, isDateRangeLessThanMonth, isIsoWeekPeriod } from '@/shared/utils/dateUtils'
import { formatInt } from '@/shared/utils/metricsUtils'
import { findLabeledExtrema } from '@/shared/utils/chartBars'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { RechartsHoverAdapter } from '@/shared/components/ui/RechartsHoverAdapter'


/**
 * System variant SKU keys that should be excluded from money losers view.
 * 'general' (Store-Wide) is excluded because it always appears as a loss
 * without proper ad attribution tracking.
 */
const SYSTEM_VARIANT_SKUS_EXCLUDED_FROM_LOSERS = new Set(['general'])

/**
 * Checks if a product key represents a system variant that should be excluded from money losers.
 * Uses the dimension group's members array to find the underlying SKU key.
 */
function isExcludedSystemVariant(
  productKey: string,
  getGroupFn: (key: string) => { members?: string[] } | undefined
): boolean {
  const group = getGroupFn(productKey)
  if (!group?.members) return false
  return group.members.some(sku => SYSTEM_VARIANT_SKUS_EXCLUDED_FROM_LOSERS.has(sku))
}

/**
 * Chart data point for profit visualization
 */
interface ProfitChartData {
  /** Period name (e.g., "Jan 2024", "Week 1") */
  period: string
  /** Profit amount */
  profit: number
}

/**
 * Props for the ProfitOverTimeChart component
 */
interface ProfitOverTimeChartProps {
  /** Additional CSS classes */
  className?: string
}

/**
 * Calculates average daily profit from data points
 * @param data - Array of profit data points
 * @param currency - ISO currency code for formatting
 * @param locale - Locale for Intl formatting
 * @returns Average profit formatted as currency per day
 */
function calculateAverageProfit(
  data: ProfitChartData[],
  currency: string,
  locale: string
): { value: number; label: string } {
  if (!data.length) {
    return { value: 0, label: `${formatMoney(0, currency, locale, 0, 0, 'narrowSymbol')}/day` }
  }
  const totalProfit = data.reduce((sum, point) => sum + point.profit, 0)
  const averageProfit = totalProfit / data.length
  const label = `${formatMoney(averageProfit, currency, locale, 0, 0, 'narrowSymbol')}/day`
  return { value: averageProfit, label }
}

/**
 * Chart configuration for ShadCn charts
 */
const createChartConfig = (t: TFunction<'features-home'>, _isOC: boolean): ChartConfig => ({
  profit: {
    label: t('charts.profitOverTime.dailyProfit', { defaultValue: 'Profit / day' }),
    color: 'var(--color-positive)',
  },
})

/**
 * Props for the local ProductRow component used in tooltip table cells.
 */
interface ProductRowProps {
  /** Product image URL */
  imageUrl?: string | null | undefined
  /** Product title (e.g., "5 × Product Name") */
  title: string
  /** Formatted value to display */
  value: string
  /** Optional color class for value */
  valueClassName?: string
  /** Whether to dim the quantity prefix in title */
  dimQuantityPrefix?: boolean
}

/**
 * Compact product row for tooltip table cells.
 * Local component to avoid coupling with shared HoverProductRow.
 */
function ProductRow({
  imageUrl,
  title,
  value,
  valueClassName,
  dimQuantityPrefix = false,
}: ProductRowProps): ReactElement {
  const [imgError, setImgError] = useState(false)
  const shouldFallback = !imageUrl || imgError
  const initials = title ? title.trim().replace(/^\W+/, '').slice(0, 2).toUpperCase() : ''

  // Parse '63 × Product Name' format if dimQuantityPrefix is enabled
  const titleParts = dimQuantityPrefix && title.includes(' × ') ? title.split(' × ') : null

  return (
    <div className="flex items-start gap-3">
      <div className="h-[55px] w-[55px] rounded-md bg-muted/20 overflow-hidden shrink-0">
        {shouldFallback ? (
          <div className="w-full h-full flex items-center justify-center text-lg font-medium text-muted-foreground">
            {initials}
          </div>
        ) : (
          <img
            src={imageUrl!}
            alt={title}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        )}
      </div>
      <div className="flex flex-col items-start gap-1 min-w-0">
        <p className="w-full text-xs font-normal leading-snug line-clamp-2 text-muted-foreground">
          {titleParts ? (
            <>
              <span className="text-muted-foreground/75">{titleParts[0]} ×</span>
              <span className="text-foreground/90 ml-1">{titleParts[1]}</span>
            </>
          ) : (
            title
          )}
        </p>
        <div className={cn('mt-0.5 font-semibold text-base text-foreground', valueClassName)}>
          {value}
        </div>
      </div>
    </div>
  )
}

/**
 * Props for ProfitTooltipContent - the shared tooltip table content
 */
interface ProfitTooltipContentProps {
  /** Period string (e.g., "2025-08-26") */
  period: string
  /** Optional total profit override (for OC basis) */
  totalProfitOverride?: number | undefined
}

/**
 * Reusable tooltip content that displays most/least profitable products for a period.
 * Used by both desktop hover tooltip and mobile bottom sheet.
 */
function ProfitTooltipContent({
  period,
  totalProfitOverride,
}: ProfitTooltipContentProps): ReactElement {
  const { t, i18n } = useTranslation('features-home')
  const { topFor, bottomFor, totalProfitFor } = useTopProductsByPeriod()
  const { getGroup } = useProductGroupsMetadata()
  const { profitBasis } = useFilter()
  const compactMoney = useCompactMoney()

  const imageFor = (sku: string): string | null | undefined => getGroup(sku)?.imageUrl ?? null

  // Weekly buckets arrive as ISO week keys (e.g. "2026-W21"); show the full
  // Mon–Sun range instead of a single ambiguous date.
  const periodLabel = isIsoWeekPeriod(period)
    ? formatIsoWeekRange(period, i18n.language)
    : formatPeriod(period, i18n.language, false)

  const { topList, bottomList, totalProfit, hasProfitData } = useMemo(() => {
    const top = topFor(period, 4)
    const bottom = bottomFor(period, 4).filter(
      p => !isExcludedSystemVariant(p.sku, getGroup)
    )
    const total = totalProfitOverride !== undefined
      ? totalProfitOverride
      : (profitBasis === 'OC' ? 0 : totalProfitFor(period))
    return {
      topList: top,
      bottomList: bottom,
      totalProfit: total,
      hasProfitData: top.length > 0 || bottom.length > 0,
    }
  }, [period, topFor, bottomFor, totalProfitFor, getGroup, profitBasis, totalProfitOverride])

  if (!hasProfitData) {
    return (
      <div className="text-xl font-semibold text-center py-8">
        {t('charts.profitOverTime.noProfitOn', { defaultValue: `No profit on ${periodLabel}` })}
      </div>
    )
  }

  return (
    <Table className="table-fixed">
      <TableHeader>
        {/* Profit/loss header */}
        <TableRow className="hover:bg-transparent border-t-0 bg-black/10">
          <TableHead colSpan={2} className="px-4 h-12 text-center">
            <span className="text-base font-light text-muted-foreground">
              {periodLabel}:{' '}
            </span>
            <span
              className={cn(
                'text-base font-semibold',
                totalProfit >= 0 ? 'text-[var(--color-positive)]' : 'text-[var(--color-negative)]'
              )}
            >
              {totalProfit >= 0 ? '+' : ''}
              {compactMoney(Math.round(totalProfit))}
            </span>
          </TableHead>
        </TableRow>
        {/* Column headers */}
        <TableRow className="hover:bg-transparent border-t-0">
          <TableHead className="w-1/2 px-4 h-8 border-r border-border">
            <span className="text-xs font-medium text-muted-foreground">
              {t('charts.profitOverTime.mostProfitable', { defaultValue: 'Most Profitable' })}
            </span>
          </TableHead>
          <TableHead className="w-1/2 px-4 h-8">
            <span className="text-xs font-medium text-muted-foreground">
              {t('charts.profitOverTime.losingProducts', { defaultValue: 'Losing Products' })}
            </span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: Math.min(Math.max(topList.length, bottomList.length, 1), 4) }).map((_, idx) => {
          const leftProduct = topList[idx]
          const rightProduct = bottomList[idx]
          return (
            <TableRow key={idx} className="hover:bg-transparent">
              <TableCell className="w-1/2 px-4 py-3 align-top border-r border-border whitespace-normal">
                {leftProduct && (
                  <ProductRow
                    imageUrl={imageFor(leftProduct.sku)}
                    title={`${formatInt(leftProduct.quantity)} × ${leftProduct.name}`}
                    value={`+${compactMoney(Math.round(leftProduct.profit))}`}
                    valueClassName="text-[var(--color-positive)]"
                    dimQuantityPrefix
                  />
                )}
              </TableCell>
              <TableCell className="w-1/2 px-4 py-3 align-top whitespace-normal">
                {rightProduct && (
                  <ProductRow
                    imageUrl={imageFor(rightProduct.sku)}
                    title={`${formatInt(rightProduct.quantity)} × ${rightProduct.name}`}
                    value={compactMoney(Math.round(rightProduct.profit))}
                    valueClassName="text-[var(--color-negative)]"
                    dimQuantityPrefix
                  />
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

/**
 * Custom tooltip props for Recharts
 */
interface ProfitTooltipProps {
  active?: boolean
  payload?: { value: number }[]
  label?: string
  /** Whether to skip rendering (for mobile where Sheet is used instead) */
  disabled?: boolean
}

/**
 * Custom tooltip component using RechartsHoverAdapter for proper animation handling
 */
function CustomTooltip(
  props: ProfitTooltipProps & { [key: string]: unknown }
): ReactElement | null {
  const { disabled = false } = props
  const { profitBasis } = useFilter()

  // Extract profit value from payload for OC basis
  const profitValue = props.payload?.[0]?.value ?? 0

  return (
    <RechartsHoverAdapter
      {...props}
      dataChartId="chart-profit-over-time"
      width={600}
      estimatedHeight={400}
      followMouse={false}
      disabled={disabled}
    >
      {({ label: tooltipLabel }) => {
        const period = tooltipLabel ? String(tooltipLabel) : ''
        if (!period) return null

        return (
          <ProfitTooltipContent
            period={period}
            totalProfitOverride={profitBasis === 'OC' ? profitValue : undefined}
          />
        )
      }}
    </RechartsHoverAdapter>
  )
}

/**
 * Formats a period string to a more readable date format
 * @param period - Period string (e.g., "2025-08-26")
 * @param locale - Current locale ('en' or 'pl')
 * @param includeYear - Whether to include the year in the output
 * @returns Formatted date string (e.g., "26 Aug 2025" or "26 Aug")
 */
function formatPeriod(period: string, locale: string = 'en', includeYear: boolean = true): string {
  try {
    // Try to parse as ISO date first
    const date = parseISO(period)
    const dateLocale = locale === 'pl' ? pl : enUS
    return format(date, includeYear ? 'd MMM yyyy' : 'd MMM', { locale: dateLocale })
  } catch {
    // If not a valid date, return the original period
    return period
  }
}

// Trend calculation removed as the footer annotation was dropped.

/**
 * Profit Over Time area chart component that displays profit trends
 * using real API data from the ecommerce statistics endpoint.
 * Hides itself when the selected date range spans only a single day.
 *
 * @component
 * @example
 * ```tsx
 * <ProfitOverTimeChart />
 * ```
 */
export function ProfitOverTimeChart({ className }: ProfitOverTimeChartProps): ReactElement {
  const { t, i18n } = useTranslation('features-home')
  const { currency: reportingCurrency } = useReportingCurrency()
  const { startDate, endDate, aggregation, profitBasis } = useFilter()
  const compactMoney = useCompactMoney()
  const isMobile = useIsMobile()

  const { data: profitSeries, isLoading: loading, isError, error } = useProfitOverTime()
  const locale = i18n.language
  const resolvedCurrency = reportingCurrency ?? 'USD'

  // Mobile: track selected period for bottom sheet
  const [mobilePeriod, setMobilePeriod] = useState<string | null>(null)
  const [mobileProfitValue, setMobileProfitValue] = useState<number>(0)
  const chartContainerRef = useRef<HTMLDivElement>(null)

  // Transform derived data to chart format
  const chartData: ProfitChartData[] = useMemo(() => {
    return (profitSeries ?? []).map(p => ({
      period: p.period,
      profit: p.profit,
    }))
  }, [profitSeries])

  // Calculate average value for reference line
  const { value: averageValue, label: averageProfitLabel } = useMemo(
    () => calculateAverageProfit(chartData, resolvedCurrency, locale),
    [chartData, resolvedCurrency, locale]
  )

  // Calculate profitable days ratio
  const profitableDaysStats = useMemo(() => {
    if (!chartData.length) return { profitable: 0, total: 0 }
    const profitable = chartData.filter(d => d.profit > 0).length
    return { profitable, total: chartData.length }
  }, [chartData])
  const chartConfig = useMemo(() => createChartConfig(t, profitBasis === 'OC'), [t, profitBasis])

  // Check if all values are zero to avoid rendering
  const isAllZero = useMemo(() => {
    return chartData.every(d => d.profit === 0)
  }, [chartData])

  // Calculate appropriate tick interval based on data length and aggregation
  const tickInterval = useMemo(() => {
    const dataLength = chartData.length

    if (aggregation === 'Daily') {
      // For daily data, show fewer ticks if there's a lot of data
      if (dataLength > 20) return Math.ceil(dataLength / 8) // Show ~8 ticks
      if (dataLength > 10) return Math.ceil(dataLength / 6) // Show ~6 ticks
      return Math.ceil(dataLength / 4) // Show ~4 ticks for smaller datasets
    }

    // For other aggregations, show more frequent ticks
    return 0 // Let Recharts decide automatically
  }, [chartData.length, aggregation])

  // Label the curve's turning points (local peaks/valleys above a
  // prominence threshold), not just the global peak and trough.
  const extrema = useMemo(
    () => findLabeledExtrema(chartData.map((d) => d.profit)),
    [chartData]
  )

  // Only show year in axis labels if date range spans multiple years
  const isCrossYear = useMemo(() => {
    if (!startDate || !endDate) return false
    return startDate.getFullYear() !== endDate.getFullYear()
  }, [startDate, endDate])

  // Mobile: handle touch on chart to find nearest data point and open sheet
  const handleMobileTouch = useCallback((clientX: number) => {
    if (!isMobile || !chartContainerRef.current || !chartData.length) return

    const rect = chartContainerRef.current.getBoundingClientRect()
    const chartLeft = rect.left + 12 // margin.left
    const chartRight = rect.right - 12 // margin.right
    const chartWidth = chartRight - chartLeft

    // Calculate which data point is closest to the touch
    const touchX = clientX - chartLeft
    const ratio = Math.max(0, Math.min(1, touchX / chartWidth))
    const index = Math.round(ratio * (chartData.length - 1))
    const dataPoint = chartData[index]

    if (dataPoint) {
      setMobilePeriod(dataPoint.period)
      setMobileProfitValue(dataPoint.profit)
    }
  }, [isMobile, chartData])

  // Add touch event listener for mobile
  useEffect(() => {
    if (!isMobile) return
    const container = chartContainerRef.current
    if (!container) return

    const handleTouch = (e: TouchEvent): void => {
      const touch = e.touches[0]
      if (e.touches.length === 1 && touch) {
        handleMobileTouch(touch.clientX)
      }
    }

    container.addEventListener('touchstart', handleTouch, { passive: true })
    return () => container.removeEventListener('touchstart', handleTouch)
  }, [isMobile, handleMobileTouch])

  // Hide when a single-day range is selected (hooks already called above)
  if (startDate && endDate && differenceInCalendarDays(endDate, startDate) === 0) {
    return <></>
  }

  // Hide when monthly aggregation is selected and date range is less than a month
  // (only 1 collapsed data point, which is shown in metric cards)
  if (aggregation === 'Monthly' && isDateRangeLessThanMonth(startDate, endDate)) {
    return <></>
  }

  if (loading) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
            {t('charts.profitOverTime.profit', { defaultValue: 'Profit' })}
          </h3>
          <div className="text-sm text-foreground/60">{t('charts.profitOverTime.loading')}</div>
        </div>
        <div className="pt-6 flex h-chart-lg items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-foreground/20 border-t-foreground" />
        </div>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
            {t('charts.profitOverTime.profit', { defaultValue: 'Profit' })}
          </h3>
          <div className="text-sm text-foreground/60">{t('charts.profitOverTime.error')}</div>
        </div>
        <div className="pt-6 flex h-chart-lg items-center justify-center text-[--color-negative]">
          {error}
        </div>
      </Card>
    )
  }

  if (!chartData.length || isAllZero) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t('charts.profitOverTime.profit', { defaultValue: 'Profit' })}
          </h3>
        </div>
        <ChartEmptyState
          variant="area"
          icon={TrendingUp}
          message={t('charts.profitOverTime.noDataForPeriod')}
          hint={t('charts.emptyHint')}
          className="mt-6 h-chart-lg"
        />
      </Card>
    )
  }

  return (
    <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
      {/* Header section */}
      <div>
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="flex items-baseline gap-2.5">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('charts.profitOverTime.profit', { defaultValue: 'Profit' })}
            </span>
            <span
              className={cn(
                'text-sm font-mono font-semibold',
                averageValue >= 0 ? 'text-[var(--color-positive)]' : 'text-[var(--color-negative)]'
              )}
            >
              {averageProfitLabel}
            </span>
          </h3>
          <div className="hidden sm:block text-sm text-muted-foreground/60">
            <span className="text-[var(--color-positive)]/70">
              {profitableDaysStats.profitable}{' '}
              {t('charts.profitOverTime.profitableDays', { defaultValue: 'profitable' })}
            </span>
            <span className="mx-1">/</span>
            <span>
              {profitableDaysStats.total - profitableDaysStats.profitable}{' '}
              {t('charts.profitOverTime.losingDays', { defaultValue: 'losing' })}
            </span>
          </div>
        </div>
      </div>

      {/* Chart section */}
      <div
        ref={chartContainerRef}
        className="pt-6"
      >
        <ChartContainer
          id="profit-over-time"
          config={chartConfig}
          className="h-chart-lg w-full relative z-30"
        >
          <AreaChart
            data={chartData}
            margin={{
              left: 12,
              right: 12,
              // Reserves room for the peak label above a dot that sits
              // exactly at dataMax (the top of the plot area).
              top: 32,
              bottom: 20,
            }}
          >
            <defs>
              <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-positive)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="var(--color-positive)" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              horizontal={true}
              stroke="var(--color-muted-foreground)"
              strokeOpacity={0.07}
              strokeDasharray="8 4"
              shapeRendering="crispEdges"
            />
            <ReferenceLine
              y={0}
              stroke="var(--color-muted-foreground)"
              strokeOpacity={0.15}
              strokeWidth={1}
            />
            <XAxis
              dataKey="period"
              tickLine={false}
              axisLine={false}
              tickMargin={20}
              tick={{ fontSize: 11, dy: 10 }}
              tickFormatter={value => formatPeriod(String(value), i18n.language, isCrossYear)}
              interval={tickInterval}
            />
            <YAxis
              orientation="right"
              tickLine={false}
              axisLine={false}
              tickMargin={13}
              tick={{ fontSize: 12, textAnchor: 'start' }}
              tickFormatter={value => {
                const formatted = compactMoney(value as number)
                return formatted.replace(/\u00a0/g, ' ')
              }}
            />
            <ChartTooltip
              cursor={false}
              content={<CustomTooltip disabled={isMobile} />}
              wrapperStyle={{ display: 'none' }}
            />
            <Area
              dataKey="profit"
              type="monotone"
              fill="url(#profitGradient)"
              stroke="var(--color-positive)"
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 6,
                stroke: 'var(--color-positive)',
                strokeWidth: 2,
                fill: 'var(--background)',
              }}
              isAnimationActive={false}
            />
            {extrema.map(({ index, kind }) => {
              const point = chartData[index]!
              const isLoss = point.profit < 0
              const dotColor = isLoss ? 'var(--color-negative)' : 'var(--color-positive)'
              // Peaks read as wins (dimmed green above), valleys as context
              // (muted below) — unless the valley is an actual loss (red).
              const labelColor =
                kind === 'max'
                  ? 'oklch(from var(--color-positive) l c h / 0.7)'
                  : isLoss
                    ? 'var(--color-negative)'
                    : 'oklch(from var(--color-muted-foreground) l c h / 0.6)'
              return (
                <ReferenceDot
                  key={`${point.period}-${kind}`}
                  x={point.period}
                  y={point.profit}
                  r={4}
                  fill={dotColor}
                  stroke={dotColor}
                  label={{
                    value: compactMoney(point.profit),
                    position: kind === 'max' ? 'top' : 'bottom',
                    fontSize: 11,
                    fontWeight: 500,
                    fill: labelColor,
                    offset: 10,
                  }}
                />
              )
            })}
          </AreaChart>
        </ChartContainer>
      </div>

      {/* Mobile: Bottom sheet for period details */}
      {isMobile && (
        <Sheet open={!!mobilePeriod} onOpenChange={(open) => !open && setMobilePeriod(null)}>
          <SheetContent
            side="bottom"
            showCloseButton={false}
            className={cn(
              'max-h-[85vh] overflow-y-auto rounded-t-2xl',
              'bg-[var(--glass-surface-dark)]'
            )}
          >
            {/* Hidden title for accessibility */}
            <SheetHeader className="sr-only">
              <SheetTitle>
                {t('charts.profitOverTime.periodDetails', { defaultValue: 'Period Details' })}
              </SheetTitle>
              <SheetDescription>
                {t('charts.profitOverTime.periodDetailsDescription', {
                  defaultValue: 'Products breakdown for the selected period',
                })}
              </SheetDescription>
            </SheetHeader>
            <div className="pb-safe">
              {mobilePeriod && (
                <ProfitTooltipContent
                  period={mobilePeriod}
                  totalProfitOverride={profitBasis === 'OC' ? mobileProfitValue : undefined}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </Card>
  )
}
